import { useState, useEffect } from 'react'
import { Calendar, Plus, X, Figma, FileText } from 'lucide-react'
import { Modal } from '@/components/ui/modal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/ui/form'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { api } from '@/services/api'
import { WorkshopTeamLinksField, EMPTY_TEAM_LINK, type WorkshopTeamLinkInput } from '@/components/projects/WorkshopTeamLinksField'
import { fetchClientsWithEmails, formatClientLabel, type ClientWithEmails } from '@/services/clients'
import { PeopleSelect } from '@/components/projects/CreateProjectModal'
import { useInternalPeople } from '@/components/projects/useInternalPeople'

export interface ActivitySector {
  id: number
  nom: string
}

export interface WorkshopFormData {
  clientId?: string
  date?: string
  consultants?: string
  /** Défini seulement si la sélection a été modifiée (sinon les consultants restent inchangés) */
  consultantIds?: string[]
  description?: string
  urlsFigma?: string[]
  urlsProposition?: string[]
  prix?: string | number
  activitySectorId?: string
  newActivitySectorName?: string  // For creating new sector
  teamLinks?: WorkshopTeamLinkInput[]
  visibleClient?: boolean
}

export interface WorkshopURL {
  id: number
  url_type: string
  url: string
  created_at: string
}

interface Workshop {
  id: number
  description: string | null
  date: string
  consultants: string | null
  consultant_ids?: string[]
  prix: number | null
  client_id: string
  activity_sector_id: number | null
  urls: WorkshopURL[]
  team_links?: Array<{ id: number; equipe: string; url: string; password: string | null }>
  visible_client?: boolean
  client_nom: string | null
  activity_sector_nom: string | null
}

export interface EditWorkshopModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: WorkshopFormData) => void | Promise<void>
  isLoading?: boolean
  workshop: Workshop
}

export function EditWorkshopModal({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
  workshop,
}: EditWorkshopModalProps) {
  const [clients, setClients] = useState<ClientWithEmails[]>([])
  const [activitySectors, setActivitySectors] = useState<ActivitySector[]>([])
  const [loadingClients, setLoadingClients] = useState(false)
  const [loadingSectors, setLoadingSectors] = useState(false)
  const [showNewSectorInput, setShowNewSectorInput] = useState(false)
  const [creatingSector, setCreatingSector] = useState(false)
  const { people, loading: loadingPeople } = useInternalPeople(open)
  const [consultantIds, setConsultantIds] = useState<string[]>([])
  const [consultantsTouched, setConsultantsTouched] = useState(false)

  const [formData, setFormData] = useState<WorkshopFormData>({
    clientId: '',
    date: '',
    consultants: '',
    description: '',
    urlsFigma: [''],
    urlsProposition: [''],
    prix: '',
    activitySectorId: '',
    newActivitySectorName: '',
    teamLinks: [{ ...EMPTY_TEAM_LINK }],
    visibleClient: false,
  })

  // Initialize form data when modal opens or workshop changes
  useEffect(() => {
    if (open && workshop) {
      const dateStr = workshop.date ? new Date(workshop.date).toISOString().split('T')[0] : ''
      const figmaUrls = workshop.urls?.filter(u => u.url_type === 'figma').map(u => u.url) || ['']
      const propositionUrls = workshop.urls?.filter(u => u.url_type === 'proposition').map(u => u.url) || ['']
      
      setFormData({
        clientId: workshop.client_id != null ? String(workshop.client_id) : '',
        date: dateStr,
        consultants: workshop.consultants || '',
        description: workshop.description || '',
        urlsFigma: figmaUrls.length > 0 ? figmaUrls : [''],
        urlsProposition: propositionUrls.length > 0 ? propositionUrls : [''],
        prix: workshop.prix !== null && workshop.prix !== undefined ? String(workshop.prix) : '',
        activitySectorId: workshop.activity_sector_id ? String(workshop.activity_sector_id) : '',
        teamLinks: workshop.team_links && workshop.team_links.length > 0
          ? workshop.team_links.map((link) => ({ equipe: link.equipe, url: link.url, password: link.password || '' }))
          : [{ ...EMPTY_TEAM_LINK }],
        visibleClient: workshop.visible_client ?? false,
      })
      setConsultantIds(workshop.consultant_ids ?? [])
      setConsultantsTouched(false)
    }
  }, [open, workshop])

  const toggleConsultant = (id: string) => {
    setConsultantsTouched(true)
    setConsultantIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    )
  }

  // Noms saisis avant la sélection par collaborateur et non rattachés à un compte
  const legacyConsultants = (() => {
    if (consultantsTouched || !workshop?.consultants) return []
    const known = new Set(
      consultantIds
        .map((id) => people.find((person) => person.id === id)?.name.toLowerCase())
        .filter(Boolean)
    )
    return workshop.consultants
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name && !known.has(name.toLowerCase()))
  })()

  // Fetch clients and activity sectors when modal opens
  useEffect(() => {
    if (open) {
      fetchClients()
      fetchActivitySectors()
    }
  }, [open])

  const fetchClients = async () => {
    setLoadingClients(true)
    try {
      setClients(await fetchClientsWithEmails())
    } catch (error) {
      console.error('Erreur lors de la récupération des clients:', error)
      setClients([])
    } finally {
      setLoadingClients(false)
    }
  }

  const fetchActivitySectors = async () => {
    setLoadingSectors(true)
    try {
      const response = await api.get<{ data: ActivitySector[]; count: number }>('/activity-sectors')
      const sectors = response.data.data || []
      setActivitySectors(sectors)
    } catch (error) {
      console.error('Erreur lors de la récupération des secteurs d\'activité:', error)
      setActivitySectors([])
    } finally {
      setLoadingSectors(false)
    }
  }

  const handleCreateSector = async () => {
    if (!formData.newActivitySectorName?.trim()) {
      alert('Veuillez entrer un nom pour le secteur d\'activité')
      return
    }

    setCreatingSector(true)
    try {
      const response = await api.post<ActivitySector>('/activity-sectors', {
        nom: formData.newActivitySectorName.trim()
      })
      const newSector = response.data
      
      // Add to list and select it
      setActivitySectors(prev => [...prev, newSector])
      setFormData(prev => ({
        ...prev,
        activitySectorId: String(newSector.id),
        newActivitySectorName: ''
      }))
      setShowNewSectorInput(false)
    } catch (error: any) {
      console.error('Erreur lors de la création du secteur:', error)
      const errorMessage = error?.message || error?.response?.data?.detail || 'Erreur lors de la création du secteur'
      alert(errorMessage)
    } finally {
      setCreatingSector(false)
    }
  }

  const handleInputChange = (field: keyof WorkshopFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  // URL management functions
  const handleUrlChange = (urlType: 'urlsFigma' | 'urlsProposition', index: number, value: string) => {
    const newUrls = [...(formData[urlType] || [''])]
    newUrls[index] = value
    setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
  }

  const addUrlField = (urlType: 'urlsFigma' | 'urlsProposition') => {
    setFormData((prev) => ({
      ...prev,
      [urlType]: [...(prev[urlType] || ['']), '']
    }))
  }

  const removeUrlField = (urlType: 'urlsFigma' | 'urlsProposition', index: number) => {
    const newUrls = [...(formData[urlType] || [''])]
    if (newUrls.length > 1) {
      newUrls.splice(index, 1)
      setFormData((prev) => ({ ...prev, [urlType]: newUrls }))
    }
  }

  const handleSubmit = async () => {
    const hasConsultants = consultantIds.length > 0 || (!consultantsTouched && Boolean(workshop?.consultants?.trim()))
    if (!formData.date?.trim() || !hasConsultants) {
      alert('Veuillez remplir tous les champs obligatoires')
      return
    }

    const { consultants: _legacyText, ...rest } = formData
    await onSubmit(consultantsTouched ? { ...rest, consultantIds } : rest)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      hideHeader={true}
      contentClassName="border-4 border-cyan-400 rounded-2xl shadow-2xl"
      footer={
        <div className="flex gap-3 pt-4 justify-start">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
            className="bg-white border-gray-200 hover:bg-gray-50"
          >
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={isLoading}
            className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
          >
            Modifier le workshop
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Modifier le Workshop</h2>
          <p className="text-sm text-gray-500">Modifiez les informations du workshop</p>
        </div>

        {/* Client Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Client</label>
          <SearchableSelect
            value={formData.clientId || ''}
            onChange={(value) => handleInputChange('clientId', value)}
            options={clients.map(client => ({
              value: client.id,
              label: formatClientLabel(client.nom, client.emails)
            }))}
            placeholder={loadingClients ? "Chargement..." : clients.length === 0 ? "Aucun client disponible" : "Sélectionner un client"}
            searchPlaceholder="Rechercher un client..."
            disabled={loadingClients || clients.length === 0}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
          />
        </div>

        {/* Consultants Qolaig */}
        <FormField label="Consultants Qolaig">
          <PeopleSelect
            people={people}
            selectedIds={consultantIds}
            onToggle={toggleConsultant}
            loading={loadingPeople}
            loadingLabel="Chargement des collaborateurs…"
            placeholder="Sélectionner un ou plusieurs consultants"
            searchPlaceholder="Rechercher un collaborateur..."
            emptyLabel="Aucun collaborateur"
          />
          {legacyConsultants.length > 0 && (
            <p className="mt-1.5 text-xs text-gray-500">
              Saisis auparavant sans compte associé : {legacyConsultants.join(', ')}. Ils restent affichés tant que
              la sélection n'est pas modifiée.
            </p>
          )}
        </FormField>

        {/* Date du workshop */}
        <FormField label="Date du workshop">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="date"
              value={formData.date || ''}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent h-12 text-base py-3 pl-10"
            />
          </div>
        </FormField>

        {/* Description */}
        <FormField label="Description (facultatif)">
          <Textarea
            value={formData.description || ''}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrivez brièvement le workshop"
            rows={3}
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent text-base py-3"
          />
        </FormField>

        {/* URLs Figma */}
        <FormField label="URLs Figma (optionnel)">
          <div className="space-y-2">
            {(formData.urlsFigma || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Figma className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsFigma', index, e.target.value)}
                    placeholder="https://figma.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsFigma?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsFigma', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsFigma')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Figma
            </button>
          </div>
        </FormField>

        {/* URLs Proposition */}
        <FormField label="URLs Proposition (optionnel)">
          <div className="space-y-2">
            {(formData.urlsProposition || ['']).map((url, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="relative flex-1">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="url"
                    value={url}
                    onChange={(e) => handleUrlChange('urlsProposition', index, e.target.value)}
                    placeholder="https://drive.google.com/..."
                    className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent pl-10"
                  />
                </div>
                {(formData.urlsProposition?.length || 0) > 1 && (
                  <button
                    type="button"
                    onClick={() => removeUrlField('urlsProposition', index)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={() => addUrlField('urlsProposition')}
              className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700"
            >
              <Plus className="w-4 h-4" />
              Ajouter une URL Proposition
            </button>
          </div>
        </FormField>

        {/* Accès workshop.qolaig.app par équipe */}
        <WorkshopTeamLinksField
          links={formData.teamLinks || []}
          visibleClient={formData.visibleClient ?? false}
          onLinksChange={(teamLinks) => setFormData((prev) => ({ ...prev, teamLinks }))}
          onVisibleClientChange={(visibleClient) => setFormData((prev) => ({ ...prev, visibleClient }))}
        />

        {/* Prix */}
        <FormField label="Prix (facultatif)">
          <Input
            type="number"
            value={formData.prix || ''}
            onChange={(e) => handleInputChange('prix', e.target.value)}
            placeholder="Prix du workshop"
            step="0.01"
            min="0"
            className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent h-12 text-base py-3"
          />
        </FormField>

        {/* Activity Sector Section */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Catégorie</label>
          
          {showNewSectorInput ? (
            <div className="space-y-4 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
              <FormField label="Nom du nouveau secteur" required>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={formData.newActivitySectorName || ''}
                    onChange={(e) => handleInputChange('newActivitySectorName', e.target.value)}
                    placeholder="Ex: E-commerce, Fintech, Santé..."
                    className="bg-white border-gray-200 focus:ring-cyan-400 focus:border-transparent flex-1"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateSector}
                    isLoading={creatingSector}
                    disabled={!formData.newActivitySectorName?.trim() || creatingSector}
                    className="bg-gradient-to-r from-cyan-400 to-cyan-500 text-white hover:from-cyan-500 hover:to-cyan-600 shadow-md"
                  >
                    Créer
                  </Button>
                </div>
              </FormField>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewSectorInput(false)}
                className="text-sm"
              >
                Annuler
              </Button>
            </div>
          ) : (
            <SearchableSelect
              value={formData.activitySectorId || ''}
              onChange={(value) => handleInputChange('activitySectorId', value)}
              options={activitySectors.map(sector => ({
                value: String(sector.id),
                label: sector.nom
              }))}
              placeholder={loadingSectors ? "Chargement..." : "Sélectionner une catégorie"}
              searchPlaceholder="Rechercher une catégorie..."
              disabled={loadingSectors}
              className="bg-gray-50 border-gray-200 focus:ring-cyan-400 focus:border-transparent"
              showAddButton={true}
              addButtonLabel="Ajouter un secteur"
              onAddClick={() => setShowNewSectorInput(true)}
            />
          )}
        </div>
      </div>
    </Modal>
  )
}

